/**
 * IndustryProfileContext — per-project profile resolution + tab-visibility hook.
 *
 * Backend is the source of truth. The hook fetches `/projects/{id}/industry-profile`
 * once per project_id and memoizes. `enabled_features` drives which sub-tabs
 * appear in the CAD-drawer Pattern B redesign.
 *
 * Failure mode: profile fetch errors fall back to "all features enabled" so a
 * transient backend hiccup doesn't blank out the drawer.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import client from '../services/apiClient';

const IndustryProfileContext = createContext(null);

// Default feature flags when the backend hasn't seeded profiles yet or the
// fetch fails. Mirrors the `general` profile so the UI doesn't blank out.
const DEFAULT_FEATURES = Object.freeze({
  bom_tab: true,
  pmi_tab: true,
  components_tab: false,
  reconciliation_tab: true,
  validation_tab: false,
  linked_items_tab: true,
  compliance_tab: true,
  suppliers_tab: true,
});

// Dual-mode (2026-07-16). Safe shape when a profile has no mode_config
// (legacy rows) or none is resolved yet — vocab lookups fall through to the
// caller's default label, so the app renders medtech/default vocabulary.
const DEFAULT_MODE_CONFIG = Object.freeze({
  vocabulary: {
    fat: 'Device Testing',
    verification: 'Design Verification',
    'verification-subtitle': 'Design review gates, test cases, inspection checklists, and V&V traceability per ISO 13485 §7.3',
    'quality-events': 'Post-Market NCR / CAPA',
  },
  corpora: ['iso_13485', 'iec_62304', 'iso_14971', 'iec_60601_1'],
  regulation_keys: ['mdr', 'iso_13485', 'iec_62304'],
  report_template: 'doc',
});

export function IndustryProfileProvider({ children }) {
  // Cache: project_id → { profile, explicit, fetchedAt, error }
  const [cache, setCache] = useState({});
  const inflight = useRef({});

  const fetchProfile = useCallback(async (projectId) => {
    if (!projectId) return null;
    if (inflight.current[projectId]) return inflight.current[projectId];

    const promise = client
      .get(`/api/v1/projects/${encodeURIComponent(projectId)}/industry-profile`)
      .then((res) => {
        const data = res?.data || {};
        setCache((prev) => ({
          ...prev,
          [projectId]: {
            profile: data.profile || null,
            explicit: !!data.explicit,
            fetchedAt: Date.now(),
            error: null,
          },
        }));
        return data.profile;
      })
      .catch((err) => {
        setCache((prev) => ({
          ...prev,
          [projectId]: {
            profile: null,
            explicit: false,
            fetchedAt: Date.now(),
            error: err?.message || 'fetch_failed',
          },
        }));
        return null;
      })
      .finally(() => {
        delete inflight.current[projectId];
      });

    inflight.current[projectId] = promise;
    return promise;
  }, []);

  const invalidate = useCallback((projectId) => {
    setCache((prev) => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ cache, fetchProfile, invalidate }),
    [cache, fetchProfile, invalidate],
  );

  return (
    <IndustryProfileContext.Provider value={value}>
      {children}
    </IndustryProfileContext.Provider>
  );
}

/**
 * Hook: returns the resolved profile + a feature-check helper.
 *
 * @param {string|null|undefined} projectId
 * @returns {{
 *   profile: object|null,
 *   features: object,
 *   featureEnabled: (key: string) => boolean,
 *   loading: boolean,
 *   error: string|null,
 *   refresh: () => Promise<void>
 * }}
 */
export function useIndustryProfile(projectId) {
  const ctx = useContext(IndustryProfileContext);
  if (ctx == null) {
    throw new Error('useIndustryProfile must be used inside <IndustryProfileProvider>');
  }
  const { cache, fetchProfile } = ctx;

  useEffect(() => {
    if (projectId && !cache[projectId]) {
      fetchProfile(projectId);
    }
  }, [projectId, cache, fetchProfile]);

  const entry = projectId ? cache[projectId] : null;
  const profile = entry?.profile || null;
  // Fall back to the default-enabled feature set so an empty/unknown profile
  // never collapses every tab.
  const features = profile?.enabled_features || DEFAULT_FEATURES;

  const featureEnabled = useCallback(
    (key) => {
      if (!features) return false;
      return !!features[key];
    },
    [features],
  );

  // M16+ truth-finding overhaul. Returns the configured authority source for
  // a specific reconciliation field (quantity / material / part_number /
  // revision / description). Cascade mirrors the backend
  // `bom_authority_for_field()` helper: per-field map → legacy single source
  // → "none". Consumers (ReconciliationPanel, e-pilot prompts) use this
  // instead of reading profile.bom_authority_by_field directly so the
  // fallback chain stays consistent.
  const authorityForField = useCallback(
    (field) => {
      if (!profile) return 'none';
      const map = profile.bom_authority_by_field;
      if (map && typeof map === 'object' && map[field]) return map[field];
      if (profile.bom_authority_source) return profile.bom_authority_source;
      return 'none';
    },
    [profile],
  );

  // Cross-source recency threshold (days). Profile-configured value with a
  // safe 14-day default for legacy profiles that haven't received the
  // backfill yet.
  const stalenessDays = profile?.bom_staleness_days ?? 14;

  // Dual-mode (2026-07-16). `modeConfig` re-skins the app per vertical.
  // `vocab(key, fallback)` returns the profile's label override for a nav id /
  // vocab key, falling through to the caller's default label — so medtech/default
  // mode (empty vocabulary) always renders the original labels.
  const modeConfig = profile?.mode_config || DEFAULT_MODE_CONFIG;
  const vocab = useCallback(
    (key, fallback) => {
      const map = modeConfig?.vocabulary;
      return (map && typeof map === 'object' && map[key]) || fallback;
    },
    [modeConfig],
  );

  return {
    profile,
    features,
    featureEnabled,
    authorityForField,
    stalenessDays,
    modeConfig,
    vocab,
    modeSlug: profile?.slug || null,
    modeName: profile?.name || null,
    loading: !!projectId && !entry,
    error: entry?.error || null,
    refresh: () => fetchProfile(projectId),
  };
}

export { DEFAULT_FEATURES, DEFAULT_MODE_CONFIG };
